//@ts-nocheck
/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNav } from '../../hooks/nav';
import { useIsDarkTheme } from '../../hooks/useIsDarkTheme';

import demo from '../../assets/images/demo.png';
import demoBlack from '../../assets/images/demoBlack.png';

import { useStore } from 'src/onboarding/store/store';
import { useMutation } from '@tanstack/react-query';

import Analytics from '@web-solutions/module-analytics';
import { useRemoteConfig } from '@web-solutions/base-app/hooks/use-remote-config';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { t } from '@web-solutions/module-localization';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import Button from 'src/onboarding/components/ui/button/Button';

import getLocale from 'src/localization/get-locale';
import { BackIcon } from 'src/onboarding/assets/icons/BackIcon';
import { Typography } from 'src/onboarding/components/typography-voery/Typography';
import { ArrowBottomIcon } from 'src/onboarding/assets/icons/ArrowBottomIcon';
import { ArrowsIcon } from 'src/onboarding/assets/icons/ArrowsIcon';
import { MicrophoneFillIcon } from 'src/onboarding/assets/icons/MicrophoneFillIcon';
import { MicrophoneOffIcon } from 'src/onboarding/assets/icons/MicrophoneOffIcon';
import { CloseIcon } from 'src/onboarding/assets/icons/CloseIcon';
import { Modal } from 'src/onboarding/components/ui/modal/Modal';
import { DropdownCheckmarkIcon } from 'src/onboarding/assets/icons/DropdownCheckmarkIcon';
import {
  ALL_LANGUAGES_KEYS,
  LANGUAGE_KEY_TO_LOCALE,
  LANGUAGE_KEY_TO_API_CODE,
} from 'src/onboarding/constants/allLanguages';
import { PalabraService } from 'src/onboarding/services/palabra.service';

import { Room, LocalAudioTrack, RoomEvent } from 'livekit-client';
import { DataPacket_Kind } from 'livekit-client';

// Сколько фраз даём перевести в демо
const PHRASES_LIMIT = 3;
// Сколько держим последний перевод на экране перед уходом со страницы
const FINISH_DELAY = 1200;
// Сколько даём дослушать перевод, прежде чем предложить следующую фразу
const PHRASE_DONE_DELAY = 3000;

function Page() {
  const { onNext, onPrev, onPaywall, currentIndex } = useNav();
  const locale = getLocale();

  const { isShowedSkipButton } = useRemoteConfig();

  // Картинка-подсказка нарисована под цвет фона, поэтому меняем её вместе с темой
  const isDarkTheme = useIsDarkTheme();

  const { nativeLanguage, setNativeLanguage, targetLanguage, setTargetLanguage } = useStore();

  const [isNativeOpen, setIsNativeOpen] = useState(false);
  const [isTargetOpen, setIsTargetOpen] = useState(false);
  const [isMicDeniedModalOpen, setIsMicDeniedModalOpen] = useState(false);

  // Состояния для Palabra AI
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState('');

  // Текущая фраза целиком: обе строки всегда относятся к одному transcription_id
  const [phrase, setPhrase] = useState({ id: null, original: '', translated: '' });
  // Сколько фраз уже перевели за сеанс на этой странице
  const [translatedCount, setTranslatedCount] = useState(0);
  // Лимит выбран: перевод уже остановлен, но текст ещё держим на экране
  const [isFinishing, setIsFinishing] = useState(false);

  const nativeDropdownRef = useRef(null);
  const targetDropdownRef = useRef(null);
  const roomRef = useRef(null);
  const mediaStreamRef = useRef(null);
  // id фраз, по которым уже пришёл перевод — чтобы не считать одну фразу дважды
  const translatedIdsRef = useRef(new Set());
  // защита от повторного завершения и таймер ухода со страницы
  const finishStartedRef = useRef(false);
  const finishTimerRef = useRef(null);
  // юзер сменил язык на активной сессии — её нужно пересоздать под новую пару
  const restartOnLanguageChangeRef = useRef(false);
  const phraseDoneTimerRef = useRef(null);

  // Языки в алфавитном порядке по переведённому названию
  const translatedLanguages = useMemo(
    () =>
      ALL_LANGUAGES_KEYS.map((key) => ({
        key,
        name: t(`onboarding.common.languages.${key}`),
      })).sort((a, b) => a.name.localeCompare(b.name, locale)),
    [locale]
  );

  // В стор язык может попасть как ключ (из этих дропдаунов) или как переведённое
  // название (со страниц native-language / language-will-you-translate) — приводим к ключу
  const resolveLanguageKey = (value) => {
    if (!value) return '';
    const byKey = translatedLanguages.find((lang) => lang.key === value);
    if (byKey) return byKey.key;
    const byName = translatedLanguages.find(
      (lang) => lang.name.toLowerCase() === String(value).toLowerCase()
    );
    return byName ? byName.key : '';
  };

  const nativeLanguageKey = resolveLanguageKey(nativeLanguage);
  const targetLanguageKey = resolveLanguageKey(targetLanguage);

  // Как только что-то распознали — вместо «Listening…» показываем текст
  const hasSpeech = Boolean(phrase.translated || phrase.original);

  // Круг занимает место кнопки, пока фраза в работе. Когда его нет — показываем
  // кнопки: «Tap and speak» и, если хоть одна фраза переведена, «Finish Demo»
  const isCircleVisible = isRecording || isFinishing;

  // Языки залочены, пока фраза в работе: сессия создана под конкретную пару.
  // Как только перевод показан — выбор снова открыт, можно взять другую пару.
  // На завершении демо лочим совсем, страница вот-вот сменится
  const isLanguageLocked =
    (isRecording && !phrase.translated) || isFinishing || translatedCount >= PHRASES_LIMIT;

  // id языков живут в БД на бэке, поэтому берём их из справочника, а не хардкодим
  const findLanguageId = (languages, langKey) => {
    if (!langKey) return null;
    const code = (
      LANGUAGE_KEY_TO_API_CODE[langKey] ||
      LANGUAGE_KEY_TO_LOCALE[langKey] ||
      ''
    ).toLowerCase();
    if (!code) return null;
    const apiLanguage = languages.find((lang) => lang.code.toLowerCase() === code);
    return apiLanguage ? apiLanguage.id : null;
  };

  // Выбранный язык всегда первым, остальные — по алфавиту
  const getOrderedLanguages = (selectedKey) => {
    if (!selectedKey) return translatedLanguages;
    const selected = translatedLanguages.find((lang) => lang.key === selectedKey);
    if (!selected) return translatedLanguages;
    return [selected, ...translatedLanguages.filter((lang) => lang.key !== selectedKey)];
  };

  const getLanguageName = (langValue) => {
    if (!langValue) return t('onboarding.tryVoery.selectLanguage');
    const key = resolveLanguageKey(langValue);
    const language = translatedLanguages.find((lang) => lang.key === key);
    return language ? language.name : langValue;
  };

  // Прогреваем справочник заранее, чтобы первый тап не ждал сеть.
  // Промис кэшируется в сервисе, так что повторные вызовы запрос не дублируют
  useEffect(() => {
    PalabraService.getLanguages().catch((err) => {
      console.error('❌ Ошибка загрузки списка языков:', err);
    });
  }, []);

  // Сессия создаётся на нашем бэке (он сам общается с Palabra по своим ключам)
  const createPalabraSession = async () => {
    try {
      setError('');
      console.log('🔄 Создаём сессию Palabra через наш бэк...');

      const languages = await PalabraService.getLanguages();
      const sourceLanguageId = findLanguageId(languages, nativeLanguageKey);
      const targetLanguageId = findLanguageId(languages, targetLanguageKey);

      if (!sourceLanguageId || !targetLanguageId) {
        throw new Error('Не удалось определить id выбранных языков');
      }

      const session = await PalabraService.createOnboardingSession({
        sourceLanguageId,
        targetLanguageId,
      });

      if (!session) {
        throw new Error('Не удалось создать сессию');
      }

      console.log('✅ Сессия создана!', session);
      setSessionData(session);
      return session;
    } catch (err) {
      console.error('❌ Ошибка создания сессии:', err);
      setError(t('onboarding.tryVoery.connectionError'));
      return null;
    }
  };

  const startTranslation = async () => {
    // Языки приходят с предыдущих шагов флоу, так что это страховка.
    // Кнопка в этом состоянии и так притушена, поэтому просто ничего не делаем
    if (!nativeLanguage || !targetLanguage) {
      return;
    }

    try {
      setError('');
      setIsRecording(true);
      // Индикатор показываем сразу по клику, ещё до запроса микрофона
      setIsConnecting(true);
      // Чистим фразу от предыдущего запуска, чтобы не висел старый текст
      setPhrase({ id: null, original: '', translated: '' });

      // 1️⃣ Сначала микрофон — при отказе нет смысла создавать сессию
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1 },
        });
      } catch (err: any) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setIsMicDeniedModalOpen(true);
          setIsConnecting(false);
          setIsRecording(false);
          return;
        }
        throw err;
      }

      mediaStreamRef.current = stream;

      // 2️⃣ Создаем Palabra session
      const session = await createPalabraSession();
      if (!session) {
        // не держим микрофон открытым, если сессии не будет
        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        setIsConnecting(false);
        setIsRecording(false);
        return;
      }

      // 3️⃣ Подключаемся к WebRTC
      const room = new Room();
      roomRef.current = room;

      await room.connect(session.webrtcUrl, session.publisher, {
        autoSubscribe: true,
      });

      setIsConnected(true);
      console.log('✅ Connected to WebRTC');

      // 4️⃣ Публикуем трек микрофона (как в документации)
      const localTrack = new LocalAudioTrack(stream.getAudioTracks()[0]);

      await room.localParticipant.publishTrack(localTrack, {
        dtx: false, // ОБЯЗАТЕЛЬНО для Palabra
        red: false,
        audioPreset: {
          maxBitrate: 32000,
          priority: 'high',
        },
      });

      console.log('🎤 Audio track published');

      // 5️⃣ Слушаем переведённый аудио трек
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === 'audio') {
          console.log('📥 Translation audio received');

          const mediaStream = new MediaStream([track.mediaStreamTrack]);
          const audio = new Audio();
          audio.srcObject = mediaStream;
          audio.play().catch(console.error);
        }
      });

      // 6️⃣ Подписка на транскрипции (и исходные, и переведенные)
      room.on(RoomEvent.DataReceived, (payload) => {
        try {
          const message = JSON.parse(new TextDecoder().decode(payload));
          if (!message.message_type) return;

          const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
          const transcription = data?.transcription;
          const text = transcription?.text || data?.translation?.text || data?.text || '';
          if (!text) return;

          // Все сообщения одной фразы приходят с общим transcription_id —
          // по нему склеиваем оригинал с переводом и считаем фразы
          const id = transcription?.transcription_id ?? null;

          switch (message.message_type) {
            // Речь юзера: partial — по ходу говорения, validated — финальный вариант
            case 'partial_transcription':
            case 'validated_transcription':
              setPhrase((prev) =>
                prev.id === id
                  ? { ...prev, original: text }
                  : { id, original: text, translated: '' }
              );
              console.log('🗣️ Original:', { id, text, type: message.message_type });
              break;

            case 'translated_transcription':
              setPhrase((prev) =>
                prev.id === id
                  ? { ...prev, translated: text }
                  : { id, original: '', translated: text }
              );
              console.log('🌍 Translated:', { id, text });

              // Фразу засчитываем один раз, на первый пришедший по ней перевод
              if (id && !translatedIdsRef.current.has(id)) {
                translatedIdsRef.current.add(id);
                const count = translatedIdsRef.current.size;
                setTranslatedCount(count);
                console.log(`✅ Переведено фраз: ${count} из ${PHRASES_LIMIT}`);
              }

              // Перевод приходит частями, поэтому таймер сдвигаем на каждый кусок:
              // ждём PHRASE_DONE_DELAY после последнего, чтобы озвучка успела отыграть.
              // Сессию до этого момента не рвём, иначе аудио перевода обрежется
              if (phraseDoneTimerRef.current) {
                clearTimeout(phraseDoneTimerRef.current);
              }
              phraseDoneTimerRef.current = setTimeout(() => {
                const count = translatedIdsRef.current.size;

                if (count >= PHRASES_LIMIT) {
                  finishDemo();
                } else if (count === 1) {
                  // Пауза только после первой фразы: даём выбрать «Finish Demo»
                  // или сказать следующую фразу заново. Дальше сессию не рвём
                  stopTranslation();
                }
              }, PHRASE_DONE_DELAY);
              break;

            default:
              break;
          }
        } catch (e) {
          console.error('Parse error:', e);
        }
      });

      // Коды берём из ответа бэка — так они точно совпадают с тем, под что создана сессия
      const sourceLang = session.sourceLanguage.code;
      const targetLang = session.targetLanguage.code;
      console.log(sourceLang, targetLang);
      // 7️⃣ Отправляем set_task (ВАЖНО — после publishTrack)
      const setTaskMessage = {
        message_type: 'set_task',
        data: {
          input_stream: {
            content_type: 'audio',
            source: { type: 'webrtc' },
          },
          output_stream: {
            content_type: 'audio',
            target: { type: 'webrtc' },
          },
          pipeline: {
            transcription: {
              source_language: sourceLang,
            },
            translations: [
              {
                target_language: targetLang,
                translate_partial_transcriptions: true,
                speech_generation: {
                  voice_id: 'default_low',
                },
              },
            ],
            // без этого списка Palabra не присылает транскрипции исходной речи
            allowed_message_types: [
              'partial_transcription',
              'validated_transcription',
              'translated_transcription',
            ],
          },
        },
      };

      const payload = new TextEncoder().encode(JSON.stringify(setTaskMessage));
      await room.localParticipant.publishData(
        payload,
        DataPacket_Kind.RELIABLE,
        'palabra:set_task'
      );
      console.log('📤 set_task sent');

      // Пайплайн собран — подключение закончилось
      setIsConnecting(false);
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError(t('onboarding.tryVoery.connectionError'));
      setIsConnecting(false);
      setIsRecording(false);
      stopTranslation();
    }
  };
  // Функция остановки перевода.
  // keepText — оставить последнюю фразу на экране (нужно при завершении демо)
  const stopTranslation = async ({ keepText = false } = {}) => {
    try {
      if (phraseDoneTimerRef.current) {
        clearTimeout(phraseDoneTimerRef.current);
        phraseDoneTimerRef.current = null;
      }

      if (roomRef.current) {
        await roomRef.current.disconnect();
        roomRef.current = null;
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      setIsRecording(false);
      setIsConnecting(false);
      setIsConnected(false);
      // счётчик фраз не сбрасываем — лимит демо общий, а не на одно нажатие
      if (!keepText) {
        setPhrase({ id: null, original: '', translated: '' });
      }
      console.log('⏹️ Перевод остановлен');
    } catch (err) {
      console.error('Ошибка при остановке:', err);
    }
  };

  // Лимит демо выбран: останавливаем сессию, последнюю фразу оставляем на экране,
  // даём её прочитать и уходим дальше по флоу.
  // Таймер держим в ref, а не в эффекте: иначе очередное сообщение от Palabra
  // перезапустит эффект и cleanup отменит уход со страницы
  const finishDemo = () => {
    if (finishStartedRef.current) return;
    finishStartedRef.current = true;

    console.log(`🏁 Лимит ${PHRASES_LIMIT} фраз выбран — завершаем демо`);
    setIsFinishing(true);
    stopTranslation({ keepText: true });

    finishTimerRef.current = setTimeout(() => {
      onNext();
    }, FINISH_DELAY);
  };

  // Юзеру хватило демо — останавливаем сессию и уходим дальше по флоу.
  // Гасим таймер авто-завершения, чтобы не уйти на следующую страницу дважды
  const handleFinishDemo = () => {
    if (finishTimerRef.current) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
    finishStartedRef.current = true;

    Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
      page: 'try-voery',
      answer: 'finish_demo',
      phrases: translatedCount,
    });

    stopTranslation();
    onNext();
  };

  // Ok — просто закрываем, доступ юзер даёт руками в настройках браузера.
  // На следующий тап по кнопке попап поднимется снова
  const handleMicDeniedOk = () => {
    setIsMicDeniedModalOpen(false);
  };

  // Крестик — уводим сразу на пэйвол, минуя остаток флоу
  const handleMicDeniedClose = () => {
    setIsMicDeniedModalOpen(false);
    Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
      page: 'try-voery',
      answer: 'mic_denied_close',
    });
    onPaywall();
  };

  // Если пару языков поменяли на живой сессии, её надо пересоздать под новую пару
  const markLanguageChanged = () => {
    if (isRecording) {
      restartOnLanguageChangeRef.current = true;
    }
  };

  // Функция для обмена языков
  const swapLanguages = () => {
    const temp = nativeLanguage;
    setNativeLanguage(targetLanguage);
    setTargetLanguage(temp);
    markLanguageChanged();
  };

  const handleNativeLanguageSelect = (langKey) => {
    setNativeLanguage(langKey);
    setIsNativeOpen(false);
    markLanguageChanged();
  };

  const handleTargetLanguageSelect = (langKey) => {
    setTargetLanguage(langKey);
    setIsTargetOpen(false);
    markLanguageChanged();
  };

  // Языки уже обновились в сторе — поднимаем новую сессию под них.
  // Делаем это в эффекте, а не в обработчике: там в замыкании ещё старая пара
  useEffect(() => {
    if (!restartOnLanguageChangeRef.current) return;
    restartOnLanguageChangeRef.current = false;

    (async () => {
      console.log('🔄 Языки сменились — пересоздаём сессию');
      await stopTranslation();
      startTranslation();
    })();
  }, [nativeLanguage, targetLanguage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nativeDropdownRef.current && !nativeDropdownRef.current.contains(event.target)) {
        setIsNativeOpen(false);
      }
      if (targetDropdownRef.current && !targetDropdownRef.current.contains(event.target)) {
        setIsTargetOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      if (finishTimerRef.current) {
        clearTimeout(finishTimerRef.current);
      }
      if (phraseDoneTimerRef.current) {
        clearTimeout(phraseDoneTimerRef.current);
      }
      // Очищаем ресурсы при размонтировании
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className={'page'}>
      <div className={'controlsHeader'}>
        {currentIndex !== 0 && (
          <div className='back' onClick={onPrev}>
            <BackIcon />
          </div>
        )}
        {isShowedSkipButton && (
          <div
            className={'skip'}
            onClick={() => {
              Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
                page: 'try-voery',
                answer: 'skip',
              });
              onNext();
            }}
          >
            {t('onboarding.tryVoery.skip')}
          </div>
        )}
      </div>

      <div className='wrapper-info'>
        <Typography variant='h1-bold' className='title'>
          {t('onboarding.tryVoery.title')}
        </Typography>

        <Typography variant='body-regular' className='description inline'>
          {t('onboarding.tryVoery.subTitle')}{' '}
          <Typography variant='body-bold'>{t('onboarding.tryVoery.threePhrases')}</Typography>{' '}
          {t('onboarding.tryVoery.test')}
        </Typography>
        <img src={isDarkTheme ? demoBlack : demo} alt='demo' className='demoIcon' />
      </div>

      <div className='controls'>
        {/* Контейнер для дропдаунов */}
        <div className={`dropdowns-row ${isLanguageLocked ? 'locked' : ''}`}>
          {/* Дропдаун родного языка */}
          <div className='dropdown-container' ref={nativeDropdownRef}>
            <div
              className={`dropdown-header ${isNativeOpen ? 'active' : ''}`}
              onClick={() => {
                if (isLanguageLocked) return;
                setIsNativeOpen(!isNativeOpen);
                setIsTargetOpen(false);
              }}
            >
              <div className='left-column'>
                <Typography variant='callout-medium' className='language-text'>
                  {getLanguageName(nativeLanguage)}
                </Typography>
                <ArrowBottomIcon className={`dropdown-arrow ${isNativeOpen ? 'rotated' : ''}`} />
              </div>
            </div>

            {/* Выпадающее меню для родного языка */}
            {isNativeOpen && (
              <div className='dropdown-menu'>
                <div className='dropdown-menu-list'>
                  {getOrderedLanguages(nativeLanguageKey).map((lang) => (
                    <label
                      key={lang.key}
                      className='dropdown-item'
                      onClick={() => handleNativeLanguageSelect(lang.key)}
                    >
                      <span className='dropdown-check'>
                        {nativeLanguageKey === lang.key && <DropdownCheckmarkIcon />}
                      </span>
                      <Typography variant='callout-medium' className='language-name'>
                        {lang.name}
                      </Typography>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Иконка стрелок между дропдаунами - кликабельна для обмена языков */}
          <div
            onClick={isLanguageLocked ? undefined : swapLanguages}
            style={{ cursor: isLanguageLocked ? 'default' : 'pointer' }}
          >
            <ArrowsIcon className='arrows-icon' />
          </div>

          {/* Дропдаун целевого языка */}
          <div className='dropdown-container' ref={targetDropdownRef}>
            <div
              className={`dropdown-header ${isTargetOpen ? 'active' : ''}`}
              onClick={() => {
                if (isLanguageLocked) return;
                setIsTargetOpen(!isTargetOpen);
                setIsNativeOpen(false);
              }}
            >
              <div className='right-column'>
                <Typography variant='callout-medium' className='language-text'>
                  {getLanguageName(targetLanguage)}
                </Typography>
                <ArrowBottomIcon className={`dropdown-arrow ${isTargetOpen ? 'rotated' : ''}`} />
              </div>
            </div>

            {/* Выпадающее меню для целевого языка */}
            {isTargetOpen && (
              <div className='dropdown-menu align-right'>
                <div className='dropdown-menu-list'>
                  {getOrderedLanguages(targetLanguageKey).map((lang) => (
                    <label
                      key={lang.key}
                      className='dropdown-item'
                      onClick={() => handleTargetLanguageSelect(lang.key)}
                    >
                      <span className='dropdown-check'>
                        {targetLanguageKey === lang.key && <DropdownCheckmarkIcon />}
                      </span>
                      <Typography variant='callout-medium' className='language-name'>
                        {lang.name}
                      </Typography>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <audio id='remote-audio' style={{ display: 'none' }} />

        {/* Круг во время подключения, ожидания речи и перевода.
            Остановить перевод тапом нельзя — только «Finish Demo» или лимит фраз */}
        {isCircleVisible ? (
          <div className='connecting-btn'>
            <span className='connecting-glow'>
              <span className='connecting-ring' />
            </span>
            <div className='connecting-text'>
              {isConnecting ? (
                <Typography variant='callout-medium'>
                  {t('onboarding.tryVoery.connecting')}
                </Typography>
              ) : hasSpeech ? (
                /* Сверху перевод, под ним — что произнёс юзер */
                <>
                  {phrase.translated && (
                    <Typography variant='callout-medium' className='speech-translated'>
                      {phrase.translated}
                    </Typography>
                  )}
                  {phrase.original && (
                    <Typography variant='callout-medium' className='speech-original'>
                      {phrase.original}
                    </Typography>
                  )}
                </>
              ) : (
                /* Ждём речь: приветствия из сессии — сверху на языке перевода,
                   снизу на языке, с которого переводим. Порядок как у самой фразы */
                <>
                  <Typography variant='callout-medium'>
                    {sessionData?.targetLanguage?.welcomeMessage ||
                      t('onboarding.tryVoery.listening')}
                  </Typography>
                  <Typography variant='callout-medium'>
                    {sessionData?.sourceLanguage?.welcomeMessage ||
                      t('onboarding.tryVoery.speakNow')}
                  </Typography>
                </>
              )}
            </div>
          </div>
        ) : (
          /* Кнопка: старт демо, а также предложение сказать следующую фразу
             после того, как юзер дослушал перевод предыдущей */
          <div
            className='speak-btn'
            onClick={startTranslation}
            style={{
              cursor: 'pointer',
              opacity: !nativeLanguage || !targetLanguage ? 0.5 : 1,
            }}
          >
            <MicrophoneFillIcon />
            <Typography variant='callout-medium'>{t('onboarding.tryVoery.tap')}</Typography>
          </div>
        )}

        {/* Не создалась сессия или не удалось подключиться — предлагаем повторить */}
        {error && !isCircleVisible && (
          <Typography variant='callout-medium' className='connection-error'>
            {error}
          </Typography>
        )}
      </div>

      {/* Только после первой фразы: одной хватило — можно закончить демо.
          Дальше кнопку не показываем, демо само завершится на третьей фразе */}
      {translatedCount === 1 && !isCircleVisible && (
        <div className='next-wrapper'>
          <Button title={t('onboarding.tryVoery.finish')} onClick={handleFinishDemo} />
        </div>
      )}

      {/* Нет доступа к микрофону */}
      <Modal isOpen={isMicDeniedModalOpen} className='mic-denied-modal'>
        <div className='mic-denied-content'>
          <div className='mic-denied-close' onClick={handleMicDeniedClose}>
            <CloseIcon />
          </div>
          <div className='mic-denied-icon'>
            <MicrophoneOffIcon />
          </div>
          <div className='mic-denied-text'>
            <Typography variant='h2-bold'>{t('onboarding.tryVoery.micDenied.title')}</Typography>
            <Typography variant='callout-medium' className='mic-denied-description'>
              {t('onboarding.tryVoery.micDenied.description')}
            </Typography>
          </div>
          <Button title={t('onboarding.tryVoery.micDenied.ok')} onClick={handleMicDeniedOk} />
        </div>
      </Modal>
    </div>
  );
}

export default Page;

//@ts-nocheck
// import { useEffect, useRef, useState } from 'react';
// import axios from 'axios';
// import { Room, LocalAudioTrack, RoomEvent, DataPacket_Kind } from 'livekit-client';

// const PALABRA_CLIENT_ID = 'f0a18a899cf6cf5498eb1aacbd09dca2';
// const PALABRA_CLIENT_SECRET = 'adc137923fef511547b65c9c222fe66bfc42e6ec18b3067cec903e4fa9ff264d';

// // Мы знаем обоих участников
// const CURRENT_USER_ID = 'userA';
// const OTHER_USER_ID = 'userB';

// export default function Page() {
//   const roomRef = useRef<Room | null>(null);
//   const mediaStreamRef = useRef<MediaStream | null>(null);

//   const [sessionA, setSessionA] = useState<any>(null);
//   const [sessionB, setSessionB] = useState<any>(null);

//   const [originalText, setOriginalText] = useState('');
//   const [translatedText, setTranslatedText] = useState('');

//   const [messages, setMessages] = useState<string[]>([]);
//   const [chatInput, setChatInput] = useState('');

//   const [isConnected, setIsConnected] = useState(false);

//   // -------------------------------
//   // 1️⃣ Создание Palabra Session
//   // -------------------------------
//   const createPalabraSession = async () => {
//     const { data } = await axios.post(
//       'https://api.palabra.ai/session-storage/session',
//       {
//         data: {
//           subscriber_count: 1,
//           publisher_can_subscribe: true,
//         },
//       },
//       {
//         headers: {
//           ClientId: PALABRA_CLIENT_ID,
//           ClientSecret: PALABRA_CLIENT_SECRET,
//           'Content-Type': 'application/json',
//         },
//       }
//     );

//     return data.data;
//   };

//   // -------------------------------
//   // 2️⃣ Создаём две Palabra-сессии
//   // -------------------------------
//   const setupSessions = async () => {
//     const a = await createPalabraSession();
//     const b = await createPalabraSession();

//     setSessionA(a);
//     setSessionB(b);
//   };

//   // -------------------------------
//   // 3️⃣ Подключение к LiveKit
//   // -------------------------------
//   const connectToLiveKit = async (mySession: any, otherSession: any) => {
//     const room = new Room();
//     roomRef.current = room;

//     await room.connect(mySession.webrtc_url, mySession.publisher, {
//       autoSubscribe: false,
//     });

//     setIsConnected(true);

//     // Публикуем микрофон
//     const stream = await navigator.mediaDevices.getUserMedia({
//       audio: { channelCount: 1 },
//     });

//     mediaStreamRef.current = stream;

//     const localTrack = new LocalAudioTrack(stream.getAudioTracks()[0]);

//     await room.localParticipant.publishTrack(localTrack, {
//       dtx: false,
//       red: false,
//       audioPreset: {
//         maxBitrate: 32000,
//         priority: 'high',
//       },
//     });

//     // -------------------------------
//     // 4️⃣ Слушаем перевод (только чужой)
//     // -------------------------------
//     room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
//       if (participant.isLocal) return; // ❗ не проигрываем свои треки
//       if (track.kind === 'audio') {
//         const mediaStream = new MediaStream([track.mediaStreamTrack]);
//         const audio = new Audio();
//         audio.srcObject = mediaStream;
//         audio.play();
//       }
//     });

//     // -------------------------------
//     // 5️⃣ Транскрипции
//     // -------------------------------
//     room.on(RoomEvent.DataReceived, (payload) => {
//       try {
//         const message = JSON.parse(new TextDecoder().decode(payload));

//         if (message.message_type === 'transcription') {
//           const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;

//           const text = data.text || data.transcription?.text || '';

//           if (text) setOriginalText(text);

//           setMessages((prev) => [
//             ...prev,
//             {
//               from: participant.identity, // кто говорил
//               original: text,
//               translated: null,
//             },
//           ]);
//         }

//         if (message.message_type === 'translated_transcription') {
//           const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;

//           const text = data.translation?.text || data.transcription?.text || '';

//           if (text) setTranslatedText(text);

//           setMessages((prev) => {
//             const last = prev[prev.length - 1];
//             return [...prev.slice(0, -1), { ...last, translated: text }];
//           });
//         }
//       } catch (e) {
//         console.error('Parse error:', e);
//       }
//     });

//     // -------------------------------
//     // 6️⃣ Отправляем set_task
//     // -------------------------------
//     const setTaskMessage = {
//       message_type: 'set_task',
//       data: {
//         input_stream: {
//           content_type: 'audio',
//           source: { type: 'webrtc' },
//         },
//         output_stream: {
//           content_type: 'audio',
//           target: { type: 'webrtc' },
//         },
//         pipeline: {
//           transcription: {
//             source_language: 'en',
//           },
//           translations: [
//             {
//               target_language: 'es',
//               translate_partial_transcriptions: true,
//               speech_generation: {
//                 voice_id: 'default_low',
//               },
//             },
//           ],
//         },
//       },
//     };

//     const payload = new TextEncoder().encode(JSON.stringify(setTaskMessage));
//     await room.localParticipant.publishData(payload, DataPacket_Kind.RELIABLE, 'palabra:set_task');
//   };

//   // -------------------------------
//   // 7️⃣ Инициализация звонка
//   // -------------------------------
//   useEffect(() => {
//     (async () => {
//       await setupSessions();
//     })();
//   }, []);

//   useEffect(() => {
//     if (!sessionA || !sessionB) return;

//     const mySession = CURRENT_USER_ID === 'userA' ? sessionA : sessionB;
//     const otherSession = CURRENT_USER_ID === 'userA' ? sessionB : sessionA;

//     connectToLiveKit(mySession, otherSession);
//   }, [sessionA, sessionB]);

//   return (
//     <div style={{ padding: 20 }}>
//       <h1>Call Page</h1>

//       {isConnected ? (
//         <>
//           <h2>Вы в звонке</h2>

//           {/* <h3>Original</h3>
//           <div>{originalText}</div>

//           <h3>Translated</h3>
//           <div>{translatedText}</div> */}

//           <h3>Live Chat</h3>
//           <div>
//             {messages.map((m, i) => (
//               <div key={i} style={{ marginBottom: 10 }}>
//                 <strong>{m.from}:</strong> {m.original}
//                 {m.translated && (
//                   <div style={{ color: '#555', marginLeft: 10 }}>→ {m.translated}</div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </>
//       ) : (
//         <div>Подключаемся...</div>
//       )}
//     </div>
//   );
// }

// import { useEffect, useRef, useState } from 'react';
// import axios from 'axios';
// import { Room, LocalAudioTrack, RoomEvent, DataPacket_Kind } from 'livekit-client';

// export default function CallTestPage() {
//   const roomRef = useRef(null);
//   const mediaStreamRef = useRef(null);

//   const [callUuid, setCallUuid] = useState('');
//   const [isConnected, setIsConnected] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [messages, setMessages] = useState([]);
//   const [error, setError] = useState('');

//   const API_BASE = 'https://stage-api.voery.app';

//   const cleanup = async () => {
//     try {
//       if (roomRef.current) {
//         await roomRef.current.disconnect();
//         roomRef.current = null;
//       }
//       if (mediaStreamRef.current) {
//         mediaStreamRef.current.getTracks().forEach((t) => t.stop());
//         mediaStreamRef.current = null;
//       }
//       setIsConnected(false);
//     } catch (e) {
//       console.error('Cleanup error', e);
//     }
//   };

//   const handleConnect = async () => {
//     if (!callUuid.trim()) {
//       setError('Введите callUuid');
//       return;
//     }

//     setError('');
//     setLoading(true);
//     setMessages([]);
//     await cleanup();

//     try {
//       // 1️⃣ Получаем сессии по UUID
//       const { data } = await axios.get(`${API_BASE}/palabra/session/call/${callUuid.trim()}`);

//       if (!data.success || !data.sessions?.length) {
//         setError('Сессии не найдены');
//         setLoading(false);
//         return;
//       }

//       const sessions = data.sessions;

//       // Определяем роли
//       const mySession = sessions.find((s) => !s.isCreator) || sessions[0];
//       const otherSession = sessions.find((s) => s.isCreator) || sessions[1] || sessions[0];

//       console.log('mySession', mySession);
//       console.log('otherSession', otherSession);

//       // 2️⃣ Подключаемся к LiveKit
//       const room = new Room();
//       roomRef.current = room;

//       await room.connect(mySession.webrtcUrl, mySession.publisher, {
//         autoSubscribe: false,
//       });

//       setIsConnected(true);

//       // 3️⃣ Публикуем микрофон
//       const stream = await navigator.mediaDevices.getUserMedia({
//         audio: { channelCount: 1 },
//       });
//       mediaStreamRef.current = stream;

//       const localTrack = new LocalAudioTrack(stream.getAudioTracks()[0]);

//       await room.localParticipant.publishTrack(localTrack, {
//         dtx: false,
//         red: false,
//       });

//       // 4️⃣ Подписываемся только на чужие аудио-треки (перевод)
//       room.on(RoomEvent.TrackPublished, async (publication, participant) => {
//         if (participant.isLocal) return;
//         if (publication.kind === 'audio') {
//           try {
//             await publication.subscribe();
//           } catch (e) {
//             console.error('Error subscribing to audio track', e);
//           }
//         }
//       });

//       room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
//         if (participant.isLocal) return;

//         if (track.kind === 'audio') {
//           const mediaStream = new MediaStream([track.mediaStreamTrack]);
//           const audio = new Audio();
//           audio.srcObject = mediaStream;
//           audio.play().catch(console.error);
//         }
//       });

//       // 5️⃣ Лайв-чат: оригинал + перевод
//       room.on(RoomEvent.DataReceived, (payload, participant) => {
//         try {
//           const decoded = new TextDecoder().decode(payload);
//           const msg = JSON.parse(decoded);

//           const from = participant?.identity || (participant.isLocal ? 'You' : 'Remote');

//           // ОРИГИНАЛ
//           if (msg.message_type === 'transcription') {
//             const rawData = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;

//             const text = rawData?.text || rawData?.transcription?.text || '';

//             if (!text) return;

//             setMessages((prev) => [
//               ...prev,
//               {
//                 from,
//                 original: text,
//                 translated: null,
//               },
//             ]);
//           }

//           // ПЕРЕВОД
//           if (msg.message_type === 'translated_transcription') {
//             const rawData = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;

//             const text = rawData?.translation?.text || rawData?.transcription?.text || '';

//             if (!text) return;

//             setMessages((prev) => {
//               if (!prev.length) {
//                 return [
//                   {
//                     from,
//                     original: '',
//                     translated: text,
//                   },
//                 ];
//               }
//               const last = prev[prev.length - 1];
//               return [
//                 ...prev.slice(0, -1),
//                 {
//                   ...last,
//                   translated: text,
//                 },
//               ];
//             });
//           }
//         } catch (e) {
//           console.error('DataReceived parse error', e);
//         }
//       });

//       // 6️⃣ Отправляем set_task
//       const setTaskMessage = {
//         message_type: 'set_task',
//         data: {
//           input_stream: {
//             content_type: 'audio',
//             source: { type: 'webrtc' },
//           },
//           output_stream: {
//             content_type: 'audio',
//             target: { type: 'webrtc' },
//           },
//           pipeline: {
//             transcription: {
//               source_language: mySession.sourceLanguage.code,
//             },
//             translations: [
//               {
//                 target_language: mySession.targetLanguage.code,
//                 translate_partial_transcriptions: true,
//                 speech_generation: {
//                   voice_id: 'default_low',
//                 },
//               },
//             ],
//           },
//         },
//       };

//       const payload = new TextEncoder().encode(JSON.stringify(setTaskMessage));
//       await room.localParticipant.publishData(
//         payload,
//         DataPacket_Kind.RELIABLE,
//         'palabra:set_task'
//       );
//     } catch (e) {
//       console.error(e);
//       setError('Ошибка подключения');
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     return () => {
//       cleanup();
//     };
//   }, []);

//   return (
//     <div style={{ padding: 20 }}>
//       <h1>Test Call Page (Invited User)</h1>

//       {!isConnected && (
//         <div style={{ marginBottom: 20 }}>
//           <input
//             value={callUuid}
//             onChange={(e) => setCallUuid(e.target.value)}
//             placeholder='Введите callUuid'
//             style={{ padding: 8, width: 300 }}
//           />
//           <button onClick={handleConnect} disabled={loading} style={{ marginLeft: 10 }}>
//             {loading ? 'Подключаемся...' : 'Connect'}
//           </button>
//         </div>
//       )}

//       {error && <div style={{ color: 'red' }}>{error}</div>}

//       {isConnected && (
//         <>
//           <h2>Live Chat (Speech Log)</h2>

//           <div
//             style={{
//               border: '1px solid #ccc',
//               padding: 10,
//               height: 250,
//               overflowY: 'auto',
//               marginBottom: 10,
//             }}
//           >
//             {messages.map((m, i) => (
//               <div key={i} style={{ marginBottom: 10 }}>
//                 <strong>{m.from}:</strong> {m.original}
//                 {m.translated && (
//                   <div style={{ color: '#555', marginLeft: 10 }}>→ {m.translated}</div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </>
//       )}
//     </div>
//   );
// }

// import { useEffect, useRef, useState } from 'react';
// import axios from 'axios';
// import { Room, LocalAudioTrack, RoomEvent, DataPacket_Kind } from 'livekit-client';

// export default function CallTwoRoomsTestPage() {
//   const roomMyRef = useRef(null);
//   const roomOtherRef = useRef(null);
//   const mediaStreamRef = useRef(null);

//   const [callUuid, setCallUuid] = useState('');
//   const [isConnected, setIsConnected] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [messages, setMessages] = useState([]);
//   const [error, setError] = useState('');

//   const API_BASE = 'https://stage-api.voery.app';

//   const cleanup = async () => {
//     try {
//       if (roomMyRef.current) {
//         await roomMyRef.current.disconnect();
//         roomMyRef.current = null;
//       }
//       if (roomOtherRef.current) {
//         await roomOtherRef.current.disconnect();
//         roomOtherRef.current = null;
//       }
//       if (mediaStreamRef.current) {
//         mediaStreamRef.current.getTracks().forEach((t) => t.stop());
//         mediaStreamRef.current = null;
//       }
//       setIsConnected(false);
//     } catch (e) {
//       console.error('Cleanup error', e);
//     }
//   };

//   const handleConnect = async () => {
//     if (!callUuid.trim()) {
//       setError('Введите callUuid');
//       return;
//     }

//     setError('');
//     setLoading(true);
//     setMessages([]);
//     await cleanup();

//     try {
//       // 1️⃣ Получаем сессии
//       const { data } = await axios.get(`${API_BASE}/palabra/session/call/${callUuid.trim()}`);

//       if (!data.success || !data.sessions?.length) {
//         setError('Сессии не найдены');
//         setLoading(false);
//         return;
//       }

//       const sessions = data.sessions;

//       // mySession — тот, за кого сейчас заходим
//       const mySession = sessions.find((s) => !s.isCreator);
//       // otherSession — вторая сторона
//       const otherSession = sessions.find((s) => s.isCreator);

//       console.log('mySession', mySession);
//       console.log('otherSession', otherSession);

//       console.log('roomNames:', mySession.webrtcRoomName, otherSession.webrtcRoomName);

//       // 2️⃣ Создаём две комнаты
//       const roomMy = new Room();
//       const roomOther = new Room();

//       roomMyRef.current = roomMy;
//       roomOtherRef.current = roomOther;

//       await roomMy.connect(mySession.webrtcUrl, mySession.publisher, {
//         autoSubscribe: false,
//       });

//       roomMy.on(RoomEvent.Disconnected, (reason) => {
//         console.log('ROOM DISCONNECTED:', reason);
//       });

//       roomOther.on(RoomEvent.TrackPublished, async (publication, participant) => {
//         if (publication.kind === 'audio' && participant.identity.includes('palabra')) {
//           console.log('Subscribing to Palabra audio:', participant.identity);
//           await publication.subscribe();
//         }
//       });

//       roomOther.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
//         if (track.kind === 'audio' && participant.identity.includes('palabra')) {
//           console.log('PLAYING TRANSLATED AUDIO from', participant.identity);
//           const mediaStream = new MediaStream([track.mediaStreamTrack]);
//           const audio = new Audio();
//           audio.srcObject = mediaStream;
//           audio.play().catch(console.error);
//         } else {
//           console.log('Skipping ORIGINAL audio from', participant.identity);
//         }
//       });

//       await roomOther.connect(otherSession.webrtcUrl, otherSession.subscriber, {
//         autoSubscribe: true,
//       });

//       setIsConnected(true);

//       // 5️⃣ Публикуем микрофон в roomMy
//       const stream = await navigator.mediaDevices.getUserMedia({
//         audio: { channelCount: 1 },
//       });
//       mediaStreamRef.current = stream;

//       const localTrack = new LocalAudioTrack(stream.getAudioTracks()[0]);

//       console.log('PUBLISH START, state =', roomMy.state);

//       await roomMy.localParticipant.publishTrack(localTrack, {
//         dtx: false,
//         red: false,
//       });
//       console.log('PUBLISH OK');

//       // Общий обработчик DataReceived (из обеих комнат)
//       const handleData = (payload, participant) => {
//         try {
//           const decoded = new TextDecoder().decode(payload);
//           const msg = JSON.parse(decoded);

//           const from = participant?.identity || (participant?.isLocal ? 'You' : 'Remote');

//           if (msg.message_type === 'transcription') {
//             const raw = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
//             const text = raw?.text || raw?.transcription?.text || '';
//             if (!text) return;

//             setMessages((prev) => [...prev, { from, original: text, translated: null }]);
//           }

//           if (msg.message_type === 'translated_transcription') {
//             const raw = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
//             const text = raw?.translation?.text || raw?.transcription?.text || '';
//             if (!text) return;

//             setMessages((prev) => {
//               if (!prev.length) {
//                 return [{ from, original: '', translated: text }];
//               }
//               const last = prev[prev.length - 1];
//               return [...prev.slice(0, -1), { ...last, translated: text }];
//             });
//           }
//         } catch (e) {
//           console.error('Data parse error', e);
//         }
//       };

//       roomMy.on(RoomEvent.DataReceived, handleData);
//       roomOther.on(RoomEvent.DataReceived, handleData);

//       // 8️⃣ Отправляем set_task в свою комнату (roomMy)
//       const setTaskMessage = {
//         message_type: 'set_task',
//         data: {
//           input_stream: {
//             content_type: 'audio',
//             source: { type: 'webrtc' },
//           },
//           output_stream: {
//             content_type: 'audio',
//             target: { type: 'webrtc' },
//           },
//           pipeline: {
//             transcription: {
//               source_language: mySession.sourceLanguage.code,
//             },
//             translations: [
//               {
//                 target_language: mySession.targetLanguage.code,
//                 translate_partial_transcriptions: true,
//                 speech_generation: {
//                   voice_id: 'default_low',
//                 },
//               },
//             ],
//           },
//         },
//       };

//       const payload = new TextEncoder().encode(JSON.stringify(setTaskMessage));
//       console.log('PUBLISH DATA START');
//       await roomMy.localParticipant.publishData(
//         payload,
//         DataPacket_Kind.RELIABLE,
//         'palabra:set_task'
//       );
//       console.log('PUBLISH DATA OK');
//     } catch (e) {
//       console.error(e);
//       setError('Ошибка подключения');
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     return () => {
//       cleanup();
//     };
//   }, []);

//   return (
//     <div style={{ padding: 20 }}>
//       <h1>Test Call Page (Two Rooms)</h1>

//       {!isConnected && (
//         <div style={{ marginBottom: 20 }}>
//           <input
//             value={callUuid}
//             onChange={(e) => setCallUuid(e.target.value)}
//             placeholder='Введите callUuid'
//             style={{ padding: 8, width: 300 }}
//           />
//           <button onClick={handleConnect} disabled={loading} style={{ marginLeft: 10 }}>
//             {loading ? 'Подключаемся...' : 'Connect'}
//           </button>
//         </div>
//       )}

//       {error && <div style={{ color: 'red' }}>{error}</div>}

//       {isConnected && (
//         <>
//           <h2>Live Chat (Speech Log)</h2>

//           <div
//             style={{
//               border: '1px solid #ccc',
//               padding: 10,
//               height: 250,
//               overflowY: 'auto',
//               marginBottom: 10,
//             }}
//           >
//             {messages.map((m, i) => (
//               <div key={i} style={{ marginBottom: 10 }}>
//                 <strong>{m.from}:</strong> {m.original}
//                 {m.translated && (
//                   <div style={{ color: '#555', marginLeft: 10 }}>→ {m.translated}</div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </>
//       )}
//     </div>
//   );
// }
